import React, { FunctionComponent, useState, useMemo } from 'react';
import { Grid, Paper, Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Button, Pagination, Skeleton, Tooltip, TextField, InputAdornment } from '@mui/material';

import DashboardLayout from '../../components/layout/layout';

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import { styled } from "@mui/material/styles";
import PersonIcon from '@mui/icons-material/Person';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LockIcon from '@mui/icons-material/Lock';
import CircleIcon from '@mui/icons-material/Circle';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import GroupIcon from '@mui/icons-material/Group';
import { useNavigate } from 'react-router-dom';
import { useGetCPAdminUser } from '../../hooks/api/user';
import { useSelector } from 'react-redux';
import { ACTIVE_COLOR, INACTIVE_COLOR } from '../../components/ui/formInput';

const ActionCard = styled(Paper)(({ theme }) => ({
  width: 180,
  height: 100,
  backgroundColor: '#f2f2f2',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '2px 2px 4px rgba(0,0,0,0.2)',
  borderRadius: 4,
  cursor: 'pointer',
  textAlign: 'center'
}));

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));

const iconStyle = { fontSize: 36, color: '#000' };

type SortField = 'firstName' | 'lastName' | 'title' | 'email' | 'phone';
type SortDirection = 'asc' | 'desc' | null;
type SortState = { field: SortField | null; direction: SortDirection };

export interface User {
  userId: number;
  firstName: string;
  lastName: string;
  title?: string;
  email: string;
  phone: string;
  cpAdmin: boolean;
  uploadInventory: boolean;
  enterOrders: boolean;
}

const AdminUser: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data: adminUser, isFetched: hasFetched } = useGetCPAdminUser();
  const detailedPermissions = useSelector((state: any) => state.permission.detailedPermissions);
  

  // Client-side pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('firstName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const itemsPerPage = 10;

  // Create buttons array with permission checking
  const adminButtons = [
   // { label: 'Verify User', icon: <PersonIcon sx={iconStyle} />, showGreenDot: true, navigateTo: 'verifyUser', visible: false },
    { label: 'Add New User', note: 'CP Admin Use Only', icon: <PersonAddAltIcon sx={iconStyle} />, navigateTo: 'add', visible: true },
    // Only show these 3 admin functions if user has BARKSDALE access level AND barksAdmin = true
    { label: 'Manage Order Status', icon: <Inventory2Icon sx={iconStyle} />, navigateTo: 'manageOrder', visible: detailedPermissions?.admin?.manageorderstatus || false },
    { label: 'Temp Password', icon: <LockIcon sx={iconStyle} />, navigateTo: 'tempPassword', visible: detailedPermissions?.admin?.temppassword || false },
    { label: 'On-Behalf Users', icon: <GroupIcon sx={iconStyle} />, navigateTo: 'on-behalf-users', visible: detailedPermissions?.admin?.onBehalfUsers || false },
  ];

  // Get all user data without pagination
  const allUserList = useMemo(() => {
    return adminUser || [];
  }, [adminUser]);

  // Filter users based on search term (email, firstName, lastName)
  const filteredUserList = useMemo(() => {
    if (!searchTerm.trim()) {
      return allUserList;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    return allUserList.filter((user: User) => {
      const email = (user.email || '').toLowerCase();
      const firstName = (user.firstName || '').toLowerCase();
      const lastName = (user.lastName || '').toLowerCase();
      
      return email.includes(searchLower) || 
             firstName.includes(searchLower) || 
             lastName.includes(searchLower);
    });
  }, [allUserList, searchTerm]);

  // Sort filtered data client-side
  const sortedData = useMemo(() => {
    if (!filteredUserList.length) return [];
    
    return [...filteredUserList].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();
      if (sortDirection === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [filteredUserList, sortField, sortDirection]);

  // Paginate sorted data
  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = sortedData.slice(startIndex, endIndex);
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);
    return { paginatedData, totalPages };
  }, [sortedData, currentPage, itemsPerPage]);

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search term change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1); // Reset to first page when clearing
  };



  // Sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sortDirection === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortDirection === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };

  const navigateUser = (userId: number, navigateTo: string) => {
    if (navigateTo === 'add') {
      navigate(`/admin/user/add`);
    }
    if (navigateTo === 'tempPassword') {
      navigate(`/admin/temp-password/create`);
    }
    // if (navigateTo === 'verifyUser') {
    //   navigate(`/admin/verify-user`);
    // }
    if (navigateTo === 'manageOrder') {
      navigate(`/admin/manage-order`);
    }
    if (navigateTo === 'on-behalf-users') {
      navigate(`/admin/on-behalf-users`);
    }
    if (navigateTo === 'edit') {
      navigate(`/admin/user/edit/${userId}`);
    }
    if (navigateTo === 'delete') {
      navigate(`/admin/user/delete/${userId}`);
    }
  };

  return (
    <>
      <DashboardLayout titleIcon={<LockOutlinedIcon />} title={"Admin"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={'CP Admin'} breadcrumbs={'User List'} activeMenu={'admin'}>
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
          <Typography variant="body1" gutterBottom sx={{ fontSize: 14 }}>
            Current Users Verified: 11/9/2020 - User list must be verified every 60 days.
          </Typography>
          <Typography variant="body1" mb={3} sx={{ fontSize: 14 }}>
            You have - <strong>1564 days</strong> left before your users get locked out.
          </Typography>

          <Grid container spacing={2} 
            sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              maxWidth: '100%',
            }}
          >
            {adminButtons.map((btn, index) => (
              <Grid key={index} onClick={() => btn.visible && navigateUser(0, btn.navigateTo)}>
                <ActionCard 
                  sx={{ 
                    border: '1px solid #e1e1e1', 
                    boxShadow: ` inset 0 0 10px rgba(0, 0, 0, 0.1), inset 0 0 5px rgba(255, 255, 255, 0.6)`,
                    opacity: btn.visible ? 1 : 0,
                    pointerEvents: btn.visible ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  <Box position="relative">
                    {btn.icon}
                    {/* {btn.showGreenDot && (
                      <CircleIcon
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -6,
                          fontSize: 12,
                          color: 'green',
                        }}
                      />
                    )} */}
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {btn.label}
                    </Typography>
                    {btn.note ? (
                      <Typography variant="caption" sx={{ color: '#c3183c', fontWeight: 600, display: 'block' }}>
                        {btn.note}
                      </Typography>
                    ) : null}
                  </Box>
                </ActionCard>
              </Grid>
            ))}
          </Grid>
        </Box>
        
        <Box sx={{ width: '100%', maxWidth: '100%', mt: 5 }}>
          {/* Search Box */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <TextField
              placeholder="Search by email, first name, or last name"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: searchTerm ? ACTIVE_COLOR : INACTIVE_COLOR }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClearSearch}
                      edge="end"
                      size="small"
                      sx={{
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(195, 24, 60, 0.08)',
                        },
                      }}
                      aria-label="clear search"
                    >
                      <ClearIcon sx={{ fontSize: 18, color: INACTIVE_COLOR }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              InputLabelProps={{
                sx: {
                  color: searchTerm ? ACTIVE_COLOR : INACTIVE_COLOR,
                  "&.Mui-focused": {
                    color: ACTIVE_COLOR,
                  },
                  "&.MuiInputLabel-shrink": {
                    color: ACTIVE_COLOR,
                  },
                  "&:hover": {
                    color: ACTIVE_COLOR,
                  },
                },
              }}
              sx={{
                minWidth: 350,
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: ACTIVE_COLOR,
                  },
                  "&:hover fieldset": {
                    borderColor: ACTIVE_COLOR,
                  },
                },
              }}
            />
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0 }}>
            <Table size="small" aria-label="user list table">
              <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                <TableRow>
                  <TableCol>Edit</TableCol>
                  <TableCol>Delete</TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('firstName')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      First Name
                      <Tooltip title="Click to sort">{getSortIcon('firstName')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('lastName')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Last Name
                      <Tooltip title="Click to sort">{getSortIcon('lastName')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('title')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Title
                      <Tooltip title="Click to sort">{getSortIcon('title')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('email')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Email
                      <Tooltip title="Click to sort">{getSortIcon('email')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol
                    sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                    onClick={() => handleSort('phone')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Phone
                      <Tooltip title="Click to sort">{getSortIcon('phone')}</Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol>CP Admin</TableCol>
                  <TableCol>Upload Inventory</TableCol>
                  <TableCol>Enter Orders</TableCol>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  hasFetched ? (
                    paginatedData.length === 0 ?
                      <TableRow>
                        <TableCol colSpan={10}>
                          No Data Found
                        </TableCol>
                      </TableRow>
                    : paginatedData.map((user: User, idx: number) => (
                      <TableRow key={user.userId} hover>
                        <TableCol>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="edit user"
                            onClick={() => navigateUser(user.userId, 'edit')}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCol>
                        <TableCol>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="delete user"
                            onClick={() => navigateUser(user.userId, 'delete')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCol>
                        <TableCol>{user.firstName}</TableCol>
                        <TableCol>{user.lastName}</TableCol>
                        <TableCol>{user.title || ""}</TableCol>
                        <TableCol>{user.email}</TableCol>
                        <TableCol>{user.phone}</TableCol>
                        <TableCol sx={{ textAlign: "center" }}>
                          {user.cpAdmin && <CheckIcon color="error" />}
                        </TableCol>
                        <TableCol sx={{ textAlign: "center" }}>
                          {user.uploadInventory && <CheckIcon color="error" />}
                        </TableCol>
                        <TableCol sx={{ textAlign: "center" }}>
                          {user.enterOrders && <CheckIcon color="error" />}
                        </TableCol>
                      </TableRow>
                    ))
                  ) : (
                    <>
                      <TableRow>
                        <TableCol colSpan={10}>
                          <Skeleton width="38%" />
                          <Skeleton width="100%" />
                          <Skeleton width="100%" />
                          <Skeleton width="61%" />
                        </TableCol>
                      </TableRow>
                    </>
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>

          {/* Back Button and Pagination */}
          <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={() => navigate('/dashboard')}>
              BACK
            </Button>
            {
              totalPages > 0 &&
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, value) => {
                  handlePageChange(value)
                }}
                siblingCount={1}
                boundaryCount={1}
                shape="rounded" 
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: "#c3183c",
                    color: "#fff",
                  },
                  "& .MuiPaginationItem-root:hover": {
                    color: "#fff",
                    backgroundColor: "#e192a3",
                  }
                }} 
              />
            }
          </Box>
        </Box>
      </DashboardLayout>
    </>
  );
};

export default AdminUser;